import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MaterialReactTable,
  MRT_RowSelectionState,
  useMaterialReactTable,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
} from "material-react-table";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { ActionItemType } from "../../utils/types";
import "../../styles/common.css";
import { Updater } from "@tanstack/react-table";

interface DataTableProps {
  header?: string;
  columns: any[];
  rows: any[];
  rowCount: number;
  pageSize: number;
  initialSort?: any;
  initialFilter?: any;
  columnVisibility?: any;
  onColumnVisibilityChange?: (visibility: Record<string, boolean>) => void;
  pinnedColumns?: { left?: string[]; right?: string[] }; // pin columns
  onPaginationChange: (pageIndex: number) => void;
  onSortingChange: (sorting: any[]) => void;
  onFilterChange?: (columnFilters: any[]) => void;
  onGlobalFilterChange?: (globalFilter: any) => void;
  renderRowActionMenuItems?: (row: any, closeMenu: any) => any;
  startEndTime?: { start_time: string; end_time: string };
  timeRange: string;
  calendarId: string | null;
  actionItemType?: string;
  onTypeChange?: (value: ActionItemType) => void;
  onPageSizeChange?: (newPageSize: number) => void;
  selectedTab?: number | string;
  onRowSelectionChange?: (selectedRows: any[]) => void;
  selectedRowIds?: any[];
  enableRowSelection?: boolean;
  showSpinner?: boolean;
  rowHoverEnabled?: boolean;
  maxHeight?: string;
  currentPageIndex?: number;
  minHeight?: string;
  tablePadding?: string;
  checkboxStyle?: { size?: "small" | "medium"; color?: string };
  disabledRowIds?: string[]; // list of row IDs where checkbox is disabled
  hiddenSelectRowIds?: string[];
  inlineCreationRow?: React.ReactNode; // custom inline creation component
  inlineCreationRowId?: string; // row ID where inline creation should replace
  maxVisibleRows?: number;
  rowHeight?: number;
  pageSizeOptions?: number[];
  headerRow?: number;
  bodyRow?: number;
  layoutMode?: "semantic" | "grid" | "grid-no-grow" | undefined;
  showVerticalDividers?: boolean;
  showRowCountLoading?: boolean;
  state?: {
    columnOrder?: string[];
  };
  onColumnOrderChange?: (newOrder: string[]) => void;
  muiTableBodyRowProps?: any;
  muiTableBodyCellProps?: any;
  enableColumnResizing?: boolean;
  enableColumnOrdering?: boolean;
  autoRowHeight?: boolean;
}


const DataTable: React.FC<DataTableProps> = ({
  header,
  columns,
  rows,
  rowCount,
  pageSize,
  initialSort,
  initialFilter,
  columnVisibility,
  onPaginationChange,
  onSortingChange,
  onFilterChange,
  onGlobalFilterChange,
  renderRowActionMenuItems,
  startEndTime,
  timeRange,
  calendarId,
  onPageSizeChange,
  onRowSelectionChange,
  selectedRowIds,
  enableRowSelection,
  showSpinner,
  rowHoverEnabled = false,
  maxHeight,
  currentPageIndex,
  minHeight,
  pinnedColumns,
  onColumnVisibilityChange,
  tablePadding,
  checkboxStyle,
  disabledRowIds,
  hiddenSelectRowIds,
  inlineCreationRow,
  inlineCreationRowId,
  maxVisibleRows,
  rowHeight,
  pageSizeOptions,
  headerRow,
  bodyRow,
  layoutMode = "grid",
  showVerticalDividers = false,
  showRowCountLoading = false,
  state,
  onColumnOrderChange,
  muiTableBodyRowProps,
  muiTableBodyCellProps,
  enableColumnResizing,
  enableColumnOrdering,
  autoRowHeight = false,
}) => {
  const theme = useTheme();
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: currentPageIndex ?? 0,
    pageSize: pageSize,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>(
    initialSort
      ? [
          {
            id: initialSort.sort_field,
            desc: initialSort.sort_order === "desc" ? true : false,
          },
        ]
      : []
  );
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    initialFilter || []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  useEffect(() => {
    setPagination({
      pageIndex: currentPageIndex ?? 0,
      pageSize: pageSize,
    });
    setSorting(
      initialSort
        ? [
            {
              id: initialSort.sort_field,
              desc: initialSort.sort_order === "desc" ? true : false,
            },
          ]
        : []
    );
    setColumnFilters(initialFilter || []);
    setGlobalFilter("");
  }, [timeRange, calendarId, currentPageIndex, pageSize]);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const controlledRowSelection = useMemo(() => {
    const selectionMap: Record<string, boolean> = {}; // Assuming row.id is string; use Record<string | number, boolean> if mixed
    if (enableRowSelection && selectedRowIds && selectedRowIds.length > 0) {
      selectedRowIds?.forEach((id) => {
        selectionMap[id] = true;
      });
    }
    return selectionMap;
  }, [selectedRowIds, enableRowSelection]);

  const allRowsDisabled = useMemo(() => {
    if (!rows?.length) return false; // no rows case
    const blockedIds = [...(disabledRowIds || []), ...(hiddenSelectRowIds || [])];
    if (!blockedIds.length) return false;
    return rows.every(row => blockedIds.includes(row.id));
  }, [rows, disabledRowIds, hiddenSelectRowIds]);
  const handleRowSelectionChange = (updater: Updater<MRT_RowSelectionState>) => {
    let newSelection: MRT_RowSelectionState;
    if (typeof updater === "function") {
      newSelection = updater(controlledRowSelection);
    } else {
      newSelection = updater;
    }
  
    // Extract selected IDs (only keys where true)
    const selectedIds = Object.keys(newSelection).filter((id) => {
      if (!newSelection[id]) return false;
      if (disabledRowIds?.includes(id)) return false;
      if (hiddenSelectRowIds?.includes(id)) return false;
      if (id === "__add_task__") return false;
      return true;
    });
  
    if (onRowSelectionChange) {
      onRowSelectionChange(selectedIds);
    }
  };
  useEffect(() => {
    onPaginationChange(pagination.pageIndex);
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (typeof onPageSizeChange === "function") {
      onPageSizeChange(pagination.pageSize); // Ensure it's callable
    } else {
      console.warn("onPageSizeChange is not a function or is undefined.");
    }
  }, [pagination.pageSize]);
  
  useEffect(() => {
    onSortingChange(sorting);
  }, [sorting]);

  useEffect(() => {
    if(onFilterChange)
    onFilterChange(columnFilters);
    setPagination({
      pageIndex: currentPageIndex ?? 0,
      pageSize: pageSize,
    });
  }, [columnFilters]);

  useEffect(() => {
    if(onGlobalFilterChange)
    onGlobalFilterChange(globalFilter);
    setPagination({
      pageIndex: currentPageIndex ?? 0,
      pageSize: pageSize,
    });
  }, [globalFilter]);

  useEffect(() => {
    if (!enableRowSelection) {
      if (onRowSelectionChange) {
        onRowSelectionChange([]); // Clear on disable
      }
    }
  }, [enableRowSelection]);

  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [pagination.pageIndex]);  
  
  const computedHeight = useMemo(() => {
    const rowH = rowHeight ?? 35; // default row height
    const visible = Math.min(rows.length, maxVisibleRows ?? 50);
  
    return rowH * visible + 60; // +60 for header + padding
  }, [rows.length, rowHeight, maxVisibleRows]);  
  const allColumnIds = useMemo(() => {
    return columns.map((col) => col.id || col.accessorKey).filter(Boolean);
  }, [columns]);
  const defaultColumnOrder = useMemo(() => {
    return enableRowSelection
      ? ['mrt-row-select', ...allColumnIds]
      : allColumnIds;
  }, [enableRowSelection, allColumnIds]);
  // Ensure pinned columns always keep selection checkbox first
  const computedPinnedColumns = enableRowSelection
  ? {
      left: ['mrt-row-select', ...(pinnedColumns?.left || [])],
      right: pinnedColumns?.right || ['mrt-row-actions'],
    }
  : pinnedColumns || { right: ['mrt-row-actions'] };
  const table = useMaterialReactTable({
    layoutMode: layoutMode ?? "grid",
    columns,
    data: rows,
    renderEmptyRowsFallback: () =>
    <Box sx={{ p: 2, textAlign: "center" }}>No Records Found</Box>,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: true,
      pagination: pagination as MRT_PaginationState,
    },
    muiPaginationProps: {
      showRowsPerPage: true,
      ...(pageSizeOptions
        ? { rowsPerPageOptions: pageSizeOptions }
        : {}
      ),
    },
    enableTopToolbar: header ? true : false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableColumnFilterModes: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableColumnOrdering: enableColumnOrdering || false,
    enableColumnResizing: enableColumnResizing || false,
    columnResizeMode: "onChange",
    enableSortingRemoval: false,
    enableRowSelection: enableRowSelection
      ? (row: any) => {
          const id = row?.id;
          if (!id) return false;
          if (disabledRowIds?.includes(id)) return false;
          if (hiddenSelectRowIds?.includes(id)) return false;
          if (id === "__add_task__") {
            return false;
          }
          return true;
        }
      : false,
    onRowSelectionChange: enableRowSelection ? handleRowSelectionChange : undefined,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    rowCount,
     // ROW CHECKBOX STYLE
     muiSelectCheckboxProps: ({ row }) => {
      const isDisabled = Boolean(disabledRowIds?.includes(row?.id));
      const isHidden = Boolean(hiddenSelectRowIds?.includes(row?.id));
    
      return {
        size: checkboxStyle?.size || "small",
        disabled: isDisabled || isHidden,
        title: isDisabled || isHidden ? "" : "Select", 
        sx: {
          visibility: isHidden ? "hidden" : "visible",
          pointerEvents: isHidden ? "none" : undefined,
          borderColor: "#C2C2CA",
          color: isDisabled
            ? "#ccc"
            : checkboxStyle?.color || "#666", // gray out disabled
          cursor: isDisabled || isHidden ? "not-allowed" : "pointer",
          "&.Mui-disabled": {
            color: "#ccc",
          },
          "&.Mui-checked": {
            color: checkboxStyle?.color || "#00C2A8",
          },
          "&.MuiCheckbox-indeterminate": {
            color: checkboxStyle?.color || "#00C2A8",
          },
          "& .MuiSvgIcon-root": {
            width:
              checkboxStyle?.size === "small"
                ? "13.375px"
                : checkboxStyle?.size === "medium"
                ? "16px"
                : "16px",
            height:
              checkboxStyle?.size === "small"
                ? "13.375px"
                : checkboxStyle?.size === "medium"
                ? "16px"
                : "16px",
          },
        },
      };
    },    
    displayColumnDefOptions: {
      'mrt-row-select': {
        size: 34,       // reduce from default ~48px to 36px
        minSize: 34,
        maxSize: 34,
      },
    },
    // HEADER CHECKBOX STYLE (Select All)
    muiSelectAllCheckboxProps: {
      size: checkboxStyle?.size || "small",
      disabled: allRowsDisabled,
      title: allRowsDisabled ? "" : "Select all",
      sx: {
        verticalAlign: "middle",
        transform: "translatex(-1.5px)", // fine-tune alignment
        color: allRowsDisabled
        ? "#ccc" : checkboxStyle?.color || "#666",
        "&.Mui-checked": {
          color: checkboxStyle?.color || "#00C2A8",
        },
        "&.MuiCheckbox-indeterminate": {
          color: checkboxStyle?.color || "#00C2A8",
        },
        "& .MuiSvgIcon-root": {
          width:
            checkboxStyle?.size === "small"
              ? "13.375px"
              : checkboxStyle?.size === "medium"
              ? "16px"
              : "16px",
          height:
            checkboxStyle?.size === "small"
              ? "13.375px"
              : checkboxStyle?.size === "medium"
              ? "16px"
              : "16px",
        },
      },
      onChange: !allRowsDisabled ? () => {
        const isIndeterminate = table.getIsSomeRowsSelected();
        const isAllSelected = table.getIsAllRowsSelected();
      
        if (isIndeterminate || isAllSelected) {
          // Unselect all
          table.toggleAllRowsSelected(false);
        } else {
          // Select only rows that are NOT disabled
          const selectableRowIds = table
            .getRowModel()
            .rows.filter(
              (row) =>
                !disabledRowIds?.includes(row.id) &&
                !hiddenSelectRowIds?.includes(row.id)
            )
            .map((row) => row.id);
      
          // Build new selection object
          const newSelection: Record<string, boolean> = {};
          selectableRowIds.forEach((id) => {
            newSelection[id] = true;
          });
      
          // Update selection all at once
          table.setRowSelection(newSelection);
        }
      } : undefined,            
    },
    state: {
      showGlobalFilter: true,
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility,
      columnPinning: computedPinnedColumns,
      rowSelection: enableRowSelection ? controlledRowSelection : {},
      columnOrder: state?.columnOrder && state.columnOrder.length > 0 ? state.columnOrder : defaultColumnOrder,
    },

    onColumnOrderChange: (updaterOrValue) => {
      if (!onColumnOrderChange) return;

      const currentOrder = state?.columnOrder && state.columnOrder.length > 0 ? state.columnOrder : defaultColumnOrder;
      const newOrder =
        typeof updaterOrValue === "function"
          ? updaterOrValue(currentOrder)
          : updaterOrValue;

      onColumnOrderChange(newOrder);
    },
    onColumnVisibilityChange: (updaterOrValue) => {
      if (!onColumnVisibilityChange) return;
  
      let newVisibility: Record<string, boolean>;
      if (typeof updaterOrValue === "function") {
        newVisibility = updaterOrValue(columnVisibility || {});
      } else {
        newVisibility = updaterOrValue;
      }
  
      onColumnVisibilityChange(newVisibility);
    },
    enableRowActions: renderRowActionMenuItems !== undefined ? true : false,
    positionActionsColumn: "last",

    renderRowActionMenuItems: ({ row, closeMenu }) => {
      if (renderRowActionMenuItems) {
        const rowActionComponent: any = renderRowActionMenuItems(
          row,
          closeMenu
        );
        return rowActionComponent;
      }
      return null;
    },
    renderTopToolbarCustomActions: () => {
      return header ? (
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {header}
        </Typography>
      ) : (
        <></>
      );
    },
    muiTablePaperProps: {
      sx: {
        width: "100%",
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.body1.fontWeight,
        color: theme.palette.text.primary,
        fontSize: theme.typography.body1.fontSize,
        [theme.breakpoints.between("md", "lg")]: {
          fontSize: "14px",
        },
        [theme.breakpoints.between("sm", "md")]: {
          fontSize: "12px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "12px",
        },
        boxShadow: "none",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#FFFFFF",
        boxShadow: "none",
        height: headerRow || "45px",
        marginTop: "0px",
        position: "sticky", // Fix the header position
        top: 0, // Stick it to the top
        /*
         * Sticky header + overlay components that animate with `transform`
         * (like our SlideOver) can trigger browser compositing glitches where
         * header text/icons flicker, turn black, or disappear after close.
         * Forcing a stable paint layer prevents this.
         */
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        zIndex: 1000,
        "&:first-of-type": {
      top: 0,
        zIndex: 1100,
      },

      "&:nth-of-type(2)": {
        top: "38px", // height of first header row
        zIndex: 1000,
      },
        },
    },
    muiTableHeadCellProps: {
      className: "mui-table-head-cell",
      sx: {
        padding: "20px 5px",
        fontFamily: 'var(--font-family)',
        fontWeight: 600,  // Set font weight to 600
        fontSize: "15px",  // Set font size to 15px
        lineHeight: "18px",  // Set line height to 18px
        letterSpacing: "0px",  // Set letter-spacing to 0px
        color: "#33333B",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
        boxShadow: "none !important",
        backgroundColor: "#FFFFFF !important",
        },
        "&[data-pinned='true']:hover": {
          backgroundColor: "#F2F2F4 !important", // Apply same hover color as regular columns
          cursor: "pointer",
        },
        [theme.breakpoints.between("md", "lg")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.between("sm", "md")]: {
          fontSize: "13px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "13px",
        },
        boxShadow: "none",
        ...(showVerticalDividers && {
          borderRight: "1px solid #C2C2CA",
          borderTop: "1px solid #C2C2CA",
          "&:last-of-type": {
            borderRight: "none",
          },
        }),
        "& .MuiTableSortLabel-root": {
          color: "#33333B",
          display: "inline-flex",
          alignItems: "center",
        },

        "& .MuiTableSortLabel-root:hover": {
          color: "#6036D6",
        },

        "& .MuiTableSortLabel-root.Mui-active": {
          color: "#6036D6",
        },

        "& .MuiTableSortLabel-icon": {
          color: "#9595A3 !important",
          opacity: 1,
          margin: "0 4px !important",
        },

        "& .cf-icon-button": {
          order: 3,
        },

        "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
          color: "#6036D6 !important",
        },

        "& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon": {
          color: "#6036D6 !important",
        },
        ...(enableColumnResizing && {
          "& .Mui-TableHeadCell-ResizeHandle-Wrapper": {
            height: "100%",
            top: 0,
            right: "-20px",
            mr: "0 !important",
            ml: "0 !important",
            px: "10px",
            justifyContent: "center",
            borderColor: "#6036D6",
            cursor: "col-resize",
            zIndex: 20,
          },
          "& .Mui-TableHeadCell-ResizeHandle-Divider": {
            opacity: 0,
            width: "1px",
            borderRadius: "999px",
            backgroundColor: "#6036D6",
            borderColor: "#6036D6",
            transform: "none",
            height: "24px",
            minHeight: "24px",
            cursor: "col-resize",
            borderWidth: "2px",
            transition: "opacity 150ms ease-in-out",
          },
          "& .Mui-TableHeadCell-ResizeHandle-Wrapper:hover, & .Mui-TableHeadCell-ResizeHandle-Wrapper:active": {
            opacity: 1,
            borderColor: "#6036D6",
            cursor: "col-resize",
          },
          "& .Mui-TableHeadCell-ResizeHandle-Wrapper:hover .Mui-TableHeadCell-ResizeHandle-Divider, & .Mui-TableHeadCell-ResizeHandle-Wrapper:active .Mui-TableHeadCell-ResizeHandle-Divider": {
            opacity: 1,
          },
        }),
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const isInlineCreationRow = row.id === inlineCreationRowId && inlineCreationRow;
      
      const defaultRowProps = {
        hover: isInlineCreationRow ? false : rowHoverEnabled, // Disable hover for inline creation row
        sx: {
          backgroundColor: "transparent",
          height: isInlineCreationRow || autoRowHeight? "auto" :  bodyRow || "40px",
          minHeight: isInlineCreationRow || autoRowHeight? "auto" : bodyRow || "40px",
          transition: "background-color 200ms ease, color 200ms ease",
          // Explicitly prevent hover effects on inline creation row
          ...(isInlineCreationRow && {
            "&:hover": { 
              backgroundColor: "transparent !important",
              cursor: "default !important",
            },
            "&:hover td, &:hover th": {
              backgroundColor: "transparent !important",
              cursor: "default !important",
            },
          }),
          // only include hover styles when enabled and not inline creation row
          ...(!isInlineCreationRow && rowHoverEnabled
            ? {
                "&:hover": { backgroundColor: "transparent" },
                "&:hover td, &:hover th": {
                  backgroundColor: "transparent !important",
                  cursor: "pointer",
                },
              }
            : !isInlineCreationRow && {
                // explicitly neutralize any hover rules when disabled
                "&:hover td, &:hover th": {
                  backgroundColor: "transparent !important",
                  cursor: "default !important",
                },
              }),
        },
      };

      const customRowProps =
        typeof muiTableBodyRowProps === "function"
          ? muiTableBodyRowProps({ row })
          : muiTableBodyRowProps || {};

      return {
        ...defaultRowProps,
        ...customRowProps,
        sx: {
          ...defaultRowProps.sx,
          ...customRowProps.sx,
        },
      };
    },
    muiTableBodyCellProps: ({ row, cell }) => {
      const isInlineCreationRow = row.id === inlineCreationRowId && inlineCreationRow;
      const isSelectCell = cell.column.id === "mrt-row-select";
      const isSerialCell = cell.column.id === "serialnumber";
      const isHostCell = isInlineCreationRow && cell.column.id === "description";
      
      const defaultCellProps = {
        className: isHostCell
          ? "mui-table-body-cell inline-creation-cell" 
          : "mui-table-body-cell",
        sx: {
          padding: isInlineCreationRow && !isSerialCell && !isSelectCell ? "0px" : "10px 5px",
          overflow: isHostCell ? "visible" : undefined,
          fontFamily: 'var(--font-family)',
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "16px",
          letterSpacing: "0px",
          color: "#43434D",
          ...(isInlineCreationRow && isSelectCell && {
            visibility: "hidden",
            pointerEvents: "none",
          }),
          ...(isHostCell && {
            position: "relative !important",
            left: "auto !important",
            right: "auto !important",
            width: "100% !important",
            minWidth: "100% !important",
            maxWidth: "100% !important",
            flex: "1 1 100% !important",
            display: "table-cell !important",
          }),
          ...(isInlineCreationRow && !isHostCell && !isSerialCell && !isSelectCell && {
            display: "none !important",
          }),
          "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          [theme.breakpoints.between("md", "lg")]: {
            fontSize: "13px",
          },
          [theme.breakpoints.between("sm", "md")]: {
            fontSize: "13px",
          },
          [theme.breakpoints.down("sm")]: {
            fontSize: "13px",
          },
          ...(showVerticalDividers && {
            borderRight: "1px solid #C2C2CA",
            "&:last-of-type": {
              borderRight: "none",
            },
          }),
        },
        // Render inline creation in the first cell
        ...(isHostCell && {
          colSpan: 100,
          children: (
            <Box sx={{ 
              width: "100%",
              overflow: "visible",
            }}>
              {inlineCreationRow}
            </Box>
          ),
        }),
      };

      const customCellProps =
        typeof muiTableBodyCellProps === "function"
          ? muiTableBodyCellProps({ row, cell })
          : muiTableBodyCellProps || {};

      return {
        ...defaultCellProps,
        ...customCellProps,
        sx: {
          ...defaultCellProps.sx,
          ...customCellProps.sx,
        },
      };
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        padding: tablePadding || "0px 10px",
        ...(maxVisibleRows || rowHeight
          ? {
              minHeight: rows.length <= (maxVisibleRows ?? 50)
                ? "auto"
                : minHeight || "calc(55vh + 30px)",
    
              maxHeight:
                rows.length <= (maxVisibleRows ?? 50)
                  ? "unset"
                  : `${computedHeight}px`, // use computed height
    
              overflowY:
                rows.length <= (maxVisibleRows ?? 50)
                  ? "visible"
                  : "auto",
            }
          : {
              // keep your ORIGINAL logic exactly
              minHeight: minHeight || "calc(55vh + 30px)",
              maxHeight: maxHeight || "calc(50vh + 50px)",
              overflowY: "auto",
            }),
        overflowY: "auto", // Allows scrolling within a fixed height
        position: "relative",
        zIndex: 0,
        marginBottom: { md: "0px", lg: "0%" }, // Responsive margin on all sides
        scrollbarWidth: "thin",
      },
    },
    muiBottomToolbarProps: {
      className: "mui-bottom-toolbar",
    },
    renderBottomToolbar: ({ }) => {
      if (showRowCountLoading) {
        return (
          <Box className="table-count-spinner"
            sx={{
              '& .MuiTablePagination-displayedRows': {
                visibility: 'hidden',
              },
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <CircularProgress 
                size={14} 
                thickness={5} 
                sx={{ 
                  color: "#666",
                  position: 'absolute',
                  right: '20px',
                }} 
              />
            </Box>
          </Box>
        );
      }
      return null;
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    muiFilterTextFieldProps: {
      // variant: "outlined",
      size: "medium",
      sx: {
        "& .MuiChip-root": {
          // make it responsive
          fontSize: "12px",
          padding: "5px",
          height: "25px",
        },
      },
    },
    muiFilterDatePickerProps: startEndTime
      ? {
          minDate: dayjs(startEndTime.start_time),
          maxDate: dayjs(startEndTime.end_time),
        }
      : undefined,
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          borderRadius: "8px",
          marginTop :"0px",
          position: "relative",
          overflow: "hidden", // Prevent header from spilling over
          zIndex: 0, // Keep the table under the header
        }}
      >
        {showSpinner && (
          <div className="absolute w-full h-[80%] flex items-center justify-center bg-white bg-opacity-50 z-10">
            <CircularProgress
              sx={{ color: "#ccc", opacity: 0.5 }}
              thickness={5}
              size={60}
            />
          </div>
        )}
        <MaterialReactTable table={table} />
        
      </Box>
    </LocalizationProvider>
  );
};

export default DataTable;
